import {AfterViewInit, Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {DocumentService} from '../../../shared/services/document.service';
import {delay, distinctUntilChanged, first, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {ReplaySubject, zip} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {Document} from '../../../shared/interface/document';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {
    DICT_TYPE_FILE_TYPE,
    DictService,
    FILE_TYPE_ALLIANZ_CERT,
    FILE_TYPE_ALLIANZ_CERT_DOCUMENTS, FILE_TYPE_ALLIANZ_CERT_FRAUDS,
    FILE_TYPE_ALLIANZ_CERT_MY_CAR,
} from '../../../shared/services/dict.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Uuid} from '../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {FilterService} from '../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';
import {DocumentPreviewComponent} from "../../../shared/components/document-preview/document-preview.component";

@Component({
  selector: 'app-all-document-list',
  templateUrl: './all-document-list.component.html',
  styleUrls: ['./all-document-list.component.scss']
})
export class AllDocumentListComponent implements AfterViewInit, OnDestroy {
  @Input() typeKeys?: string[] = null;
  @Input() masterModule?: string = null;
  @Input() excludeColumns: string[] = null;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  readonly FILE_TYPE_ALLIANZ_CERT_FRAUDS = FILE_TYPE_ALLIANZ_CERT_FRAUDS;
  readonly FILE_TYPE_ALLIANZ_CERT = FILE_TYPE_ALLIANZ_CERT;
  readonly FILE_TYPE_ALLIANZ_CERT_MY_CAR = FILE_TYPE_ALLIANZ_CERT_MY_CAR;
  readonly FILE_TYPE_ALLIANZ_CERT_DOCUMENTS = FILE_TYPE_ALLIANZ_CERT_DOCUMENTS;

  initialFilters: any;
  documents: Document[] = [];
  displayedColumns: string[] = ['name', 'type', 'createdAt', 'updatedAt', 'user',
    'partnerOrOfwca', 'contractType', 'insuranceCompany', 'product' , 'actions', ];
  totalData = 0;
  typeIds?: number[] = null;
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private documentService: DocumentService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
    private dictService: DictService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.documentService.getDocumentListSubject()
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<Document>) => {
          this.documents = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      contains$filename: 'string',
      type: 'int',
      gte$createdAt: 'date',
      lte$createdAt: 'date',
      username: 'string',
      partner: 'string',
      ofwca: 'string',
      contractType: 'int',
      archived: 'int',
      in$insuranceCompany: 'int[]',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.typeKeys = this.typeKeys ?? data.typeKeys ?? null;
        this.masterModule = this.masterModule ?? data.masterModule ?? null;
        this.excludeColumns = this.excludeColumns ?? data.excludeColumns ?? [];

        if (this.excludeColumns.length > 0) {
          this.displayedColumns = this.displayedColumns.filter((col: string) => !this.excludeColumns.includes(col));
        }

        if (null === this.typeKeys) {
          this.subscribeRouteParams();
        } else {
          this.typeIds = [];
          const subscriptions = [];

          this.typeKeys.forEach((typeKey: string) => {
            subscriptions.push(this.dictService.getId(typeKey, DICT_TYPE_FILE_TYPE));
          });

          zip(...subscriptions)
            .subscribe((typeIds: number[]) => {
              this.typeIds = typeIds;
              this.subscribeRouteParams();
            });
        }
      });

    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          ...this.filtersForm.value
        })]);
      });
  }

  subscribeRouteParams(): void {
    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.loadListData();
      })
    ;
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.documentService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    if (this.typeIds !== null) {
      filters.in$type = this.typeIds;
    }

    return filters;
  }

  downloadReport(): void {
    this.documentService.downloadReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  downloadFile(uuid: string): void {
    this.documentService.downloadFile(uuid);
  }

  deleteDocument(document: Document): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć dokument?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.documentService.deleteDocument(document.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto dokument', '', {duration: 2000});
            this.loadListData();
          });
      }
    });
  }
  controlTest(): boolean{
    if ( window.location.href.includes('rejestracje')){
      return true;
    }
    return false;
  }
  openDocument(document: Document): void {
    const dialogRef = this.dialog.open(DocumentPreviewComponent, {
      maxWidth: '70vw',
      minWidth: '50vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: {document: document},
    });
  }
}
